import { defineMessages, IntlShape } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import type { PromoPostRequestErrorCode } from '../types';
import { promoPostNeedsMediaPlatforms } from '../promoPostErrors';
import { postSettingsErrorMessage } from '../postSettingsErrors';
import { xMonthLimitMessage } from '../xPostsLimit';

/** The `cause` of a refused post write, as `customBaseQuery` puts it on the error (`m24Limit`). */
function causeOf(error: unknown): Record<string, unknown> | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const cause = (error as { m24Limit?: unknown }).m24Limit;
  return typeof cause === 'object' && cause !== null ? (cause as Record<string, unknown>) : null;
}

/** The cause code of a refused post write, or null. */
export function postErrorCode(error: unknown): PromoPostRequestErrorCode | 'M24_LIMIT_QUOTA' | null {
  const code = causeOf(error)?.code;
  return typeof code === 'string' ? (code as PromoPostRequestErrorCode | 'M24_LIMIT_QUOTA') : null;
}

/** True for a 402: the month has no scheduled post left. */
export function isQuotaError(error: unknown): boolean {
  if (postErrorCode(error) === 'M24_LIMIT_QUOTA') {
    return true;
  }
  return error instanceof FYMAPIError && error.statusCode === 402;
}

const messages = defineMessages({
  timePast: {
    id: 'app.sml.promote.calendar.error.timePast',
    defaultMessage: 'The time of this post is in the past. Change the time first.',
  },
  notDraft: {
    id: 'app.sml.promote.calendar.error.notDraft',
    defaultMessage: 'This post is not a draft any more. The Calendar shows its new state.',
  },
  cancelFirst: {
    id: 'app.sml.promote.calendar.error.cancelFirst',
    defaultMessage: 'Cancel this post first. Then you can delete it.',
  },
  notDeletable: {
    id: 'app.sml.promote.calendar.error.notDeletable',
    defaultMessage: 'This post cannot be deleted.',
  },
  countChanged: {
    id: 'app.sml.promote.calendar.error.countChanged',
    defaultMessage: 'The drafts of this day changed. Check them and try again.',
  },
  needsMedia: {
    id: 'app.sml.promote.calendar.error.needsMedia',
    defaultMessage: 'This post needs a photo or a video first. Add it in the Composer.',
  },
  publishing: {
    id: 'app.sml.promote.calendar.error.publishing',
    defaultMessage: 'This post is going out now, so it cannot change.',
  },
  generic: { id: 'app.sml.promote.calendar.error.generic', defaultMessage: 'That did not work. Try again.' },
});

/** One sentence for a refused post write. */
export function postErrorMessage(intl: IntlShape, error: unknown): string {
  if (promoPostNeedsMediaPlatforms(error) !== null) {
    return intl.formatMessage(messages.needsMedia);
  }
  const settingsMessage = postSettingsErrorMessage(intl, error);
  if (settingsMessage !== undefined) {
    return settingsMessage;
  }
  // A 409 of the X month limit is not "going out now": the post stays a draft.
  const xMessage = xMonthLimitMessage(intl, error);
  if (xMessage !== undefined) {
    return xMessage;
  }
  switch (postErrorCode(error)) {
    case 'M24_POST_TIME_PAST':
      return intl.formatMessage(messages.timePast);
    case 'M24_POST_NOT_DRAFT':
      return intl.formatMessage(messages.notDraft);
    case 'M24_POST_CANCEL_FIRST':
      return intl.formatMessage(messages.cancelFirst);
    case 'M24_POST_NOT_DELETABLE':
      return intl.formatMessage(messages.notDeletable);
    case 'M24_SCHEDULE_COUNT_CHANGED':
      return intl.formatMessage(messages.countChanged);
    // The settings codes are answered above. A quota refusal and an answer with no code get the
    // general copy below.
    case 'M24_POST_SETTINGS_REQUIRED':
    case 'M24_POST_SETTINGS_INVALID':
    case 'M24_TIKTOK_WATERMARK':
    case 'M24_X_MONTH_LIMIT':
    case 'M24_LIMIT_QUOTA':
    case null:
      break;
  }
  if (error instanceof FYMAPIError && error.statusCode === 409) {
    return intl.formatMessage(messages.publishing);
  }
  return intl.formatMessage(messages.generic);
}
